import { useCallback, useEffect, useRef, useState } from 'react';
import { artworkCmd } from '../services/apiClient';
import { zip } from '../lib/zip';
import { corsReadUrl } from '../services/mediaRead';

/**
 * Exports drawn by the render service rather than in this tab: PDF, JPG, MP4 and GIF.
 *
 * PNG and SVG stay in the browser; raster exports run in a cancellable worker. These go to the cloud because
 * the browser cannot make them yet — the print writer, the video encoder and ffmpeg all
 * live in flobi-artwork-render — and because it is the same path the agent uses, so what
 * a person downloads here is what the agent would deliver.
 *
 * Progress is asked for rather than listened to: `artwork.export.get` every half second
 * is the fallback the backend documents, and it needs nothing from the live socket.
 */

export type CloudFormat = 'pdf' | 'jpg' | 'mp4' | 'gif';

export interface CloudExportState {
  readonly format: CloudFormat;
  readonly status: 'QUEUED' | 'RENDERING' | 'READY' | 'FAILED' | 'CANCELLED';
  readonly progress: number;
  readonly error?: string;
  readonly warnings: readonly string[];
  readonly jobId?: string;
}

interface JobView {
  jobId: string;
  status: CloudExportState['status'];
  progress: number;
  error?: string;
  warnings: string[];
  files: { name: string; url?: string }[];
}

const OPTIONS: Record<CloudFormat, Record<string, unknown>> = {
  pdf: { pdf: { bleedMm: 0, cropMarks: false } },
  jpg: { scale: 2, quality: 90 },
  mp4: { scale: 1, fps: 30 },
  gif: { scale: 1, fps: 15 },
};

/** Saves a link under a name. Only a same-origin or blob: link is saved; any other is opened. */
function click(url: string, name: string, newTab = false): void {
  const link = document.createElement('a');
  link.href = url;
  link.download = name;
  link.rel = 'noopener';
  if (newTab) link.target = '_blank';
  document.body.appendChild(link);
  link.click();
  link.remove();
}

/**
 * Saves one file. The link is the file's lasting media link (24 Oct), which carries no
 * "save as" and is on another origin, so the browser ignores `download` and OPENS it: an
 * MP4 or a PDF replaced the editor in its own tab (27 Oct). So the bytes are read here and
 * saved from a blob; if storage will not let this page read them, the file opens in a new
 * tab rather than over the editor.
 */
async function save(url: string, name: string): Promise<void> {
  try {
    const response = await fetch(corsReadUrl(url));
    if (!response.ok) throw new Error(`${response.status}`);
    const blobUrl = URL.createObjectURL(await response.blob());
    click(blobUrl, name);
    setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
  } catch {
    click(url, name, true);
  }
}

/**
 * Several files as one download: fetched and zipped here, so fifty artboards are one file
 * rather than fifty prompts. If the files cannot be read from this page — the storage
 * refusing a cross-origin read — each is saved on its own instead, which is slower to
 * accept but loses nothing.
 */
async function saveAll(files: { name: string; url: string }[], zipName: string): Promise<void> {
  try {
    const read = await Promise.all(
      files.map(async (file) => {
        const response = await fetch(corsReadUrl(file.url));
        if (!response.ok) throw new Error(`${response.status}`);
        return { name: file.name, bytes: new Uint8Array(await response.arrayBuffer()) };
      }),
    );
    const blob = new Blob([zip(read) as BlobPart], { type: 'application/zip' });
    const url = URL.createObjectURL(blob);
    click(url, zipName);
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch {
    for (const file of files) click(file.url, file.name, true);
  }
}

export function useCloudExport(documentId: string) {
  const [state, setState] = useState<CloudExportState | null>(null);
  const stopped = useRef(false);
  /** The job somebody pressed Cancel on: whatever the server says next, nothing downloads. */
  const cancelled = useRef<string | null>(null);
  /**
   * Cancel pressed while the job was still being made, before it had an id: the job is
   * cancelled the moment it does. Cancel did nothing then, and the file still downloaded
   * (review E2).
   */
  const cancelSoon = useRef(0);
  /** Each start's own number, so a Cancel meant for one start never lands on the next (review R1). */
  const starts = useRef(0);
  const [fetchingAgain, setFetchingAgain] = useState(false);
  const fetching = useRef(false);
  /**
   * The last export that finished, kept while this file is open so it can be downloaded
   * again without drawing it again — a browser that blocked the download left no way to get
   * the file but exporting once more (QA 2026-10-05, ART-UX-19; Mohamad's pick, 2026-10-06).
   */
  const [ready, setReady] = useState<{ jobId: string; format: CloudFormat; zipName: string } | null>(null);
  useEffect(() => setReady(null), [documentId]);
  useEffect(
    () => () => {
      stopped.current = true;
    },
    [],
  );

  const start = useCallback(
    async (format: CloudFormat, targets: { pageId: string; artboardId: string }[] | null, zipName = 'artboards.zip', scale?: number) => {
      stopped.current = false;
      const mine = ++starts.current;
      setState({ format, status: 'QUEUED', progress: 0, warnings: [] });
      try {
        let job = await artworkCmd<JobView>('artwork.export.create', {
          documentId,
          format,
          ...OPTIONS[format],
          // The size chosen in the panel, over the format's own.
          ...(scale ? { scale } : {}),
          ...(targets && targets.length ? { artboards: targets } : {}),
        });
        if (cancelSoon.current === mine) {
          cancelled.current = job.jobId;
          await artworkCmd('artwork.export.cancel', { jobId: job.jobId }).catch(() => undefined);
          setState({ format, status: 'CANCELLED', progress: 0, warnings: [], jobId: job.jobId });
          return;
        }
        setState({ format, status: job.status, progress: job.progress, warnings: [], jobId: job.jobId });
        while (!stopped.current && cancelled.current !== job.jobId && (job.status === 'QUEUED' || job.status === 'RENDERING')) {
          await new Promise((resolve) => setTimeout(resolve, 500));
          if (cancelled.current === job.jobId) break;
          job = await artworkCmd<JobView>('artwork.export.get', { jobId: job.jobId });
          if (cancelled.current === job.jobId) break;
          setState({ format, status: job.status, progress: job.progress, warnings: job.warnings, jobId: job.jobId, ...(job.error ? { error: job.error } : {}) });
        }
        // Cancelled here: said at once, and a render that finished meanwhile is not saved.
        if (cancelled.current === job.jobId) {
          setState({ format, status: 'CANCELLED', progress: job.progress, warnings: [], jobId: job.jobId });
          return;
        }
        if (job.status === 'READY') {
          const files = job.files.filter((file): file is { name: string; url: string } => !!file.url);
          if (files.length === 1) await save(files[0]!.url, files[0]!.name);
          else if (files.length > 1) await saveAll(files, zipName);
          if (files.length > 0) setReady({ jobId: job.jobId, format, zipName });
        }
      } catch (error) {
        setState({ format, status: 'FAILED', progress: 100, warnings: [], error: (error as Error).message });
      }
    },
    [documentId],
  );

  const cancel = useCallback(async () => {
    if (!state) return;
    if (!state.jobId) {
      if (state.status !== 'QUEUED') return;
      cancelSoon.current = starts.current;
      setState((was) => (was && !was.jobId ? { ...was, status: 'CANCELLED' } : was));
      return;
    }
    cancelled.current = state.jobId;
    setState((was) => (was && was.jobId === state.jobId ? { ...was, status: 'CANCELLED' } : was));
    await artworkCmd('artwork.export.cancel', { jobId: state.jobId }).catch(() => undefined);
  }, [state]);

  /** The last finished export, saved again — its links asked for afresh, since they expire. */
  const again = useCallback(async () => {
    if (!ready || fetching.current) return;
    fetching.current = true;
    setFetchingAgain(true);
    try {
      const job = await artworkCmd<JobView>('artwork.export.get', { jobId: ready.jobId });
      const files = job.files.filter((file): file is { name: string; url: string } => !!file.url);
      if (files.length === 0) throw new Error('That export is no longer kept. Export it again.');
      if (files.length === 1) await save(files[0]!.url, files[0]!.name);
      else await saveAll(files, ready.zipName);
    } finally {
      fetching.current = false;
      setFetchingAgain(false);
    }
  }, [ready]);

  return { state, start, cancel, ready, again, fetchingAgain, dismiss: () => setState(null) };
}
